/**
 * Control-room authentication: username + password + MANDATORY TOTP (a recovery code is accepted
 * instead and yields a RESTRICTED session that can only re-enrol TOTP). First access is through a
 * single-use setup link from the bootstrap CLI: `/setup#<token>` — the token stays in the fragment,
 * so it never reaches server logs or Referer headers.
 */
import { Button, Card, HopexLockup, PasswordField, StateView, TextField } from '@hopex/ui';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Copy, Download } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { encode } from 'uqr';
import { api, errorMessage } from '../api.ts';
import { useAdminSession } from '../session.tsx';

export function AuthFrame({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid min-h-dvh place-items-center bg-canvas px-4 py-10 text-fg">
      <main id="main" className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-3">
          <HopexLockup size={24} decorative />
          <span className="rounded-xs border border-line px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
            ADMIN
          </span>
        </div>
        <Card className="p-6">
          <h1 className="m-0 text-xl font-bold">{title}</h1>
          {subtitle && (
            <p className="mt-1.5 mb-0 text-[13px] leading-6 text-fg-muted">{subtitle}</p>
          )}
          <div className="mt-5">{children}</div>
        </Card>
      </main>
    </div>
  );
}

function ErrorNote({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p
      role="alert"
      className="m-0 rounded-md border border-negative/40 bg-negative-tint px-3.5 py-2.5 text-[13px] text-negative"
    >
      {text}
    </p>
  );
}

export function QrCode({ text, label }: { text: string; label: string }) {
  const qr = useMemo(() => encode(text, { ecc: 'M', border: 2 }), [text]);
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${qr.size} ${qr.size}`}
      className="size-44 rounded-md bg-white p-1"
      shapeRendering="crispEdges"
    >
      {qr.data.flatMap((row, y) =>
        row.map((on, x) =>
          on ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="#000" /> : null,
        ),
      )}
    </svg>
  );
}

export function RecoveryCodesPanel({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const text = codes.join('\n');
  const [copied, setCopied] = useState(false);
  return (
    <div data-recovery-codes className="flex flex-col gap-3">
      <p className="m-0 flex items-start gap-2 text-[13px] font-semibold leading-6">
        <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-pending" />
        کدهای بازیابی فقط همین یک بار نمایش داده می‌شوند. هر کد یک بار کار می‌کند و ورود با آن فقط
        اجازه‌ی ثبت دوباره‌ی برنامه‌ی Authenticator را می‌دهد.
      </p>
      <ol className="m-0 grid list-none grid-cols-2 gap-2 p-0 font-mono text-[14px]" dir="ltr">
        {codes.map((c) => (
          <li key={c} className="rounded-md bg-surface-3 px-3 py-2 text-center">
            {c}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="sm"
          icon={<Copy aria-hidden className="size-4" />}
          onPress={() => void navigator.clipboard.writeText(text).then(() => setCopied(true))}
        >
          {copied ? 'کپی شد' : 'کپی'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon={<Download aria-hidden className="size-4" />}
          onPress={() => {
            const url = URL.createObjectURL(
              new Blob([`HopEx admin recovery codes\n\n${text}\n`], { type: 'text/plain' }),
            );
            const a = document.createElement('a');
            a.href = url;
            a.download = 'hopex-admin-recovery-codes.txt';
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          دانلود
        </Button>
        <Button size="sm" onPress={onDone}>
          آن‌ها را ذخیره کردم
        </Button>
      </div>
    </div>
  );
}

function TotpEnrol({ secret, uri }: { secret: string; uri: string }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <QrCode text={uri} label="کد QR برای افزودن حساب مدیر به برنامه‌ی Authenticator" />
      <div
        className="w-full rounded-md bg-surface-3 px-3 py-2 text-center font-mono text-[13px] tracking-wider"
        dir="ltr"
        data-totp-secret
      >
        {secret.match(/.{1,4}/g)?.join(' ')}
      </div>
    </div>
  );
}

export function AdminLoginPage() {
  const { refresh } = useAdminSession();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthFrame
      title="ورود به اتاق کنترل"
      subtitle={
        pendingToken
          ? 'کد ۶ رقمی برنامه‌ی Authenticator را وارد کنید.'
          : 'نام کاربری و رمز عبور مدیر.'
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            if (!pendingToken) {
              setPendingToken(
                (await api.post<{ pendingToken: string }>('/auth/login', { username, password }))
                  .pendingToken,
              );
            } else {
              await api.post('/auth/login/verify', { pendingToken, code });
              await refresh();
              await navigate({ to: '/' });
            }
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        {!pendingToken ? (
          <>
            <TextField
              label="نام کاربری"
              value={username}
              onChange={setUsername}
              ltr
              autoComplete="username"
              isRequired
            />
            <PasswordField
              label="رمز عبور"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              isRequired
            />
          </>
        ) : (
          <TextField
            label="کد ورود دومرحله‌ای یا کد بازیابی"
            value={code}
            onChange={setCode}
            ltr
            autoComplete="one-time-code"
            isRequired
            description="ورود مدیر بدون کد دومرحله‌ای ممکن نیست."
          />
        )}
        <ErrorNote text={error} />
        <Button type="submit" fullWidth size="lg" loading={busy}>
          {pendingToken ? 'تأیید و ورود' : 'ادامه'}
        </Button>
      </form>
    </AuthFrame>
  );
}

export function AdminSetupPage() {
  const navigate = useNavigate();
  const [token] = useState(() => window.location.hash.slice(1));
  const [info, setInfo] = useState<{ username: string; secret: string; otpauthUri: string } | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(
    token ? null : 'لینک راه‌اندازی معتبر نیست.',
  );
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // Drop the token from the address bar and history as soon as it has been read.
    window.history.replaceState(null, '', window.location.pathname);
    if (!token) return;
    let live = true;
    api
      .post<{ username: string; secret: string; otpauthUri: string }>('/setup/info', { token })
      .then(
        (r) => live && setInfo(r),
        (e: unknown) => live && setLoadError(errorMessage(e)),
      );
    return () => {
      live = false;
    };
  }, [token]);

  if (loadError)
    return (
      <AuthFrame title="راه‌اندازی حساب مدیر">
        <StateView
          kind="error"
          title="این لینک قابل استفاده نیست"
          description={`${loadError} لینک راه‌اندازی یک‌بارمصرف و زمان‌دار است؛ لینک تازه را از مدیر سیستم بگیرید.`}
        />
      </AuthFrame>
    );
  if (codes)
    return (
      <AuthFrame title="حساب مدیر آماده است">
        <RecoveryCodesPanel codes={codes} onDone={() => void navigate({ to: '/login' })} />
      </AuthFrame>
    );
  if (!info)
    return (
      <AuthFrame title="راه‌اندازی حساب مدیر">
        <p aria-busy="true" className="m-0 text-fg-muted">
          در حال بارگذاری…
        </p>
      </AuthFrame>
    );
  return (
    <AuthFrame title="راه‌اندازی حساب مدیر" subtitle={`نام کاربری: ${info.username}`}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            setCodes(
              (
                await api.post<{ recoveryCodes: string[] }>('/setup/complete', {
                  token,
                  password,
                  totpCode: code,
                })
              ).recoveryCodes,
            );
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="m-0 text-[13px] leading-6">
          ۱. این کد را در برنامه‌ی Authenticator اسکن کنید (ورود دومرحله‌ای برای مدیر اجباری است).
        </p>
        <TotpEnrol secret={info.secret} uri={info.otpauthUri} />
        <p className="m-0 text-[13px] leading-6">
          ۲. رمز عبور مدیر را تعیین کنید و کد ۶ رقمی برنامه را وارد کنید.
        </p>
        <PasswordField
          label="رمز عبور مدیر"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          isRequired
          description="حداقل ۱۲ کاراکتر؛ فقط برای همین حساب."
        />
        <TextField
          label="کد ۶ رقمی برنامه"
          value={code}
          onChange={setCode}
          ltr
          inputMode="numeric"
          autoComplete="one-time-code"
          isRequired
        />
        <ErrorNote text={error} />
        <Button type="submit" fullWidth size="lg" loading={busy}>
          فعال‌سازی حساب
        </Button>
      </form>
    </AuthFrame>
  );
}

/** A RESTRICTED session (signed in with a recovery code) can only register a new authenticator. */
export function AdminReenrolPage() {
  const { signOut } = useAdminSession();
  const navigate = useNavigate();
  const [enrol, setEnrol] = useState<{
    secret: string;
    otpauthUri: string;
    reenrolToken: string;
  } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (codes)
    return (
      <AuthFrame
        title="برنامه‌ی Authenticator تازه ثبت شد"
        subtitle="همه‌ی نشست‌ها بسته شدند؛ دوباره وارد شوید."
      >
        <RecoveryCodesPanel
          codes={codes}
          onDone={() => void signOut().then(() => navigate({ to: '/login' }))}
        />
      </AuthFrame>
    );
  return (
    <AuthFrame
      title="ثبت دوباره‌ی ورود دومرحله‌ای"
      subtitle="با کد بازیابی وارد شده‌اید. تا ثبت برنامه‌ی Authenticator تازه، دسترسی دیگری ندارید."
    >
      {!enrol ? (
        <div className="flex flex-col gap-3">
          <ErrorNote text={error} />
          <Button
            fullWidth
            loading={busy}
            onPress={async () => {
              setBusy(true);
              setError(null);
              try {
                setEnrol(await api.post('/auth/reenrol/begin'));
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            شروع ثبت
          </Button>
          <Button
            variant="ghost"
            fullWidth
            onPress={() => void signOut().then(() => navigate({ to: '/login' }))}
          >
            خروج
          </Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              setCodes(
                (
                  await api.post<{ recoveryCodes: string[] }>('/auth/reenrol/complete', {
                    reenrolToken: enrol.reenrolToken,
                    totpCode: code,
                  })
                ).recoveryCodes,
              );
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <TotpEnrol secret={enrol.secret} uri={enrol.otpauthUri} />
          <TextField
            label="کد ۶ رقمی برنامه"
            value={code}
            onChange={setCode}
            ltr
            inputMode="numeric"
            autoComplete="one-time-code"
            isRequired
          />
          <ErrorNote text={error} />
          <Button type="submit" fullWidth loading={busy}>
            ثبت
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}
